import React from 'react'
import { USER_ACQUISITION_QUERY_PARAM_MAP } from '#src/services/tracking'
import HttpStatusCodes from './typings'
import { ResponseConsumer } from '#src/context/response'
import { isMobileNative } from '#src/services/mobile'
import { relativeToAbsoluteUrl } from '#src/services/navigation'
import { createLocation } from 'history'
import {
  Redirect as ReactRedirect,
  RedirectProps as ReactRedirectProps,
  withRouter,
  RouteComponentProps
} from 'react-router-dom'

interface RedirectProps extends RouteComponentProps<{}>, ReactRedirectProps {
  statusCode?: HttpStatusCodes
  ignoreSearchParams?: boolean
}

const Redirect: React.SFC<RedirectProps> = ({
  statusCode,
  history,
  ignoreSearchParams,
  to,
  ...rest
}) => {
  // If the redirect isn't done on SSR, just redirect normally
  if (process.env.TARGET === 'web' && statusCode !== HttpStatusCodes.MOVED_PERMANENTLY) {
    return <ReactRedirect to={to} {...rest} />
  }

  // If this is a redirect within mobile native, just redirect normally
  if (isMobileNative()) {
    return <ReactRedirect to={to} {...rest} />
  }

  // Convert the 'to' into a Location if it is provided as a string literal
  to = typeof to === 'string' ? createLocation(to) : to
  const toLocationSearchParams = new URLSearchParams(to.search)
  const fromLocationSearchParams = new URLSearchParams(history.location.search)

  // Copy the existing ("from") acquisition query params over to the destination ("to") Location.
  //   We do this so that these parameters are not lost in a server-side redirect before we have
  //   had a chance to parse & track them on the client-side.
  fromLocationSearchParams.forEach((queryParameterValue, queryParameterName) => {
    if (USER_ACQUISITION_QUERY_PARAM_MAP.hasOwnProperty(queryParameterName)) {
      toLocationSearchParams.append(queryParameterName, queryParameterValue)
    }
  })

  if (statusCode === HttpStatusCodes.MOVED_PERMANENTLY) {
    // When performing a 301, we must use the fully qualified url
    const absoluteUrlWithoutParams = relativeToAbsoluteUrl(to.pathname || '')

    if (process.env.TARGET === 'web') {
      // Modify window.location.href directly
      window.location.href = absoluteUrlWithoutParams + '?' + toLocationSearchParams.toString()
      return null
    } else {
      return (
        <ResponseConsumer>
          {context => {
            if (context) {
              context.redirect(
                {
                  pathname: absoluteUrlWithoutParams,
                  search: toLocationSearchParams.toString()
                },
                HttpStatusCodes.MOVED_PERMANENTLY
              )
            }

            return null
          }}
        </ResponseConsumer>
      )
    }
  }

  return (
    <ReactRedirect
      to={{
        ...to,
        search: toLocationSearchParams.toString()
      }}
      {...rest}
    />
  )
}

export { HttpStatusCodes }
export default withRouter(Redirect)
